import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { Message } from "@redotech/redo-model/agents/llm-messages";
import { AiSlideOutChatGlobal } from "@redotech/redo-web/ai/chat/components/slide-out-chat-modal-global";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { ObjectId } from "bson";
import { memo, useCallback, useContext, useEffect, useState } from "react";
import { useIntercom } from "react-use-intercom";
import { SupportHelpContext } from "../support-help-context";

type SupportThread = {
  _id: string;
  updatedAt: string | Date;
  messages: Message[];
};

export type HelpChatGlobalProps = {
  onChatOpen?: () => void;
  onLoadThread?: (threadId: string, thread: SupportThread) => void;
  onRegisterTriageThreadCallback?: (
    callback: (threadId: string) => void,
  ) => void | (() => void);
  onClose?: () => void;
  onToggle?: () => void;
};

export const HelpChatGlobal = memo(function HelpChatGlobal({
  onChatOpen,
  onLoadThread,
  onRegisterTriageThreadCallback,
  onClose,
  onToggle,
}: HelpChatGlobalProps) {
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [threadsRefreshKey, setThreadsRefreshKey] = useState(0);
  const { setOnHelpClick } = useContext(SupportHelpContext);
  const team = useRequiredContext(TeamContext);
  const user = useRequiredContext(UserContext);
  const intercom = useIntercom();
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const refreshThreads = useCallback(() => {
    setThreadsRefreshKey((prev) => prev + 1);
  }, []);

  const handleNewTriageThread = useCallback(
    (threadId: string) => {
      setCurrentThreadId(threadId);
      refreshThreads();
    },
    [refreshThreads],
  );

  useEffect(() => {
    if (onRegisterTriageThreadCallback) {
      const cleanup = onRegisterTriageThreadCallback(handleNewTriageThread);
      if (typeof cleanup === "function") {
        return cleanup;
      }
      return undefined;
    }
    return undefined;
  }, [onRegisterTriageThreadCallback, handleNewTriageThread]);

  const threadsLoad = useLoad(async () => {
    if (!user.email) {
      return { threads: [] };
    }
    return await merchantRpcClient.getMerchantSupportAgentThreads({
      userEmail: user.email,
    });
  }, [merchantRpcClient, user.email, threadsRefreshKey]);

  const { clearMessages, clearAssets, setInputValue, stopStream, addMessage } =
    useContext(AiChatContext);

  const startNewConversation = useCallback(() => {
    onChatOpen?.();
    stopStream();
    clearAssets();
    clearMessages();
    setInputValue("");
    addMessage({
      id: new ObjectId().toString(),
      role: "assistant",
      content: "Hi! What can I help you with today?",
    } as any);
    setCurrentThreadId(undefined);
  }, [
    onChatOpen,
    stopStream,
    clearAssets,
    clearMessages,
    setInputValue,
    addMessage,
  ]);

  const handleHelpClick = useCallback(() => {
    onToggle?.(); // Toggle the slide panel
    startNewConversation();
  }, [onToggle, startNewConversation]);

  const handleNewConversation = useCallback(() => {
    // Start a new conversation without toggling the slide panel
    startNewConversation();
  }, [startNewConversation]);

  useEffect(() => {
    if (team.settings.globalAI) {
      setOnHelpClick(handleHelpClick);
      return () => setOnHelpClick(undefined);
    }
    return undefined;
  }, [team.settings.globalAI, setOnHelpClick, handleHelpClick]);

  const openIntercomAction = useCallback(() => {
    onClose?.();
    intercom.showMessages();
  }, [onClose, intercom]);

  const belowChatButtons = [
    { text: "Message history with team", onClick: openIntercomAction },
  ];

  const loadThreadIntoChat = useCallback(
    (threadId: string, thread: SupportThread) => {
      stopStream();
      clearAssets();
      clearMessages();
      setInputValue("");

      for (const [index, message] of thread.messages.entries()) {
        if (message.role === "tool") continue;

        const rawContent: any = (message as any).content;
        const contentToUse =
          typeof rawContent === "string" || Array.isArray(rawContent)
            ? rawContent
            : String(rawContent ?? "");

        if (typeof contentToUse === "string" && contentToUse.trim() === "") {
          continue;
        }

        addMessage({
          id: message.id ?? new ObjectId().toString() + index,
          role: message.role,
          content: contentToUse as any,
        } as any);
      }

      onLoadThread?.(threadId, thread);
      setCurrentThreadId(threadId);
    },
    [
      stopStream,
      clearAssets,
      clearMessages,
      setInputValue,
      addMessage,
      onLoadThread,
    ],
  );

  if (!team.settings.globalAI) {
    return null;
  }

  const threads = threadsLoad.value?.threads || [];

  return (
    <AiSlideOutChatGlobal
      belowChatButtons={belowChatButtons}
      currentThreadId={currentThreadId}
      onClose={onClose || (() => {})}
      onLoadThread={loadThreadIntoChat}
      onNewConversation={handleNewConversation}
      threads={threads}
    />
  );
});
